/*
 * PackStudio — the creator's study-material packs.
 *
 * A pack is a collection of reading pages (intro + tabbed notes + a question
 * set — the same page shape as the Knowledge Hub), organized into sections.
 * Publishing lists it on the creator's storefront and in every learner's
 * Knowledge Hub → Library, where they subscribe to read and practice it.
 *
 * Pages are authored with the same HubNodeEditor as My Hub — only the
 * persistence API differs (pack pages live on the pack, not the user hub).
 */
import React, { useCallback, useEffect, useState } from 'react';
import APIService from '../../../services/APIService';
import HubNodeEditor from '../HubNodeEditor';

const groupPages = (pages) => {
  const by = new Map();
  (pages || []).forEach((p) => {
    const s = p.section || 'Pages';
    if (!by.has(s)) by.set(s, []);
    by.get(s).push(p);
  });
  return [...by.entries()];
};

/* ── one pack: meta, publish, sections of pages, page editor ─────────────── */
const PackEditor = ({ pack: initial, onBack }) => {
  const [pack, setPack] = useState(initial);
  const [meta, setMeta] = useState({ title: initial.title, tagline: initial.tagline || '' });
  const [editingPage, setEditingPage] = useState(null);   // 'new' | page object
  const [section, setSection] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    const { pack: p } = await APIService.creatorGetPack(initial.id);
    setPack(p);
  }, [initial.id]);

  const saveMeta = async () => {
    if (!meta.title.trim()) { setErr('The pack needs a title.'); return; }
    setBusy(true); setErr('');
    try {
      const { pack: p } = await APIService.creatorPatchPack(pack.id, meta);
      setPack(p);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const togglePublish = async () => {
    setBusy(true); setErr('');
    try {
      const { pack: p } = await APIService.creatorPatchPack(pack.id, {
        status: pack.status === 'published' ? 'draft' : 'published',
      });
      setPack(p);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const removePage = async (p) => {
    if (!window.confirm(`Delete page "${p.name}"?`)) return;
    try { await APIService.creatorDeletePackPage(pack.id, p.id); await reload(); }
    catch (e) { setErr(e.message); }
  };

  if (editingPage) {
    const isNew = editingPage === 'new';
    const api = {
      create: async (payload) => {
        const { page } = await APIService.creatorAddPackPage(pack.id, { ...payload, section });
        return { node: page };
      },
      update: async (id, payload) => {
        const { page } = await APIService.creatorUpdatePackPage(pack.id, id, { ...payload, section });
        return { node: page };
      },
      compose: (name, brief) => APIService.creatorComposePackPage(name, brief),
      generateQuestions: (id) => APIService.creatorGeneratePackQuestions(pack.id, id),
    };
    return (
      <div className="ca-panel">
        <div className="ca-panel-row">
          <div className="ca-panel-h">{pack.title}</div>
          <button className="ca-ghost" type="button" onClick={() => setEditingPage(null)}>← Back to the pack</button>
        </div>
        <label className="ca-field pk-section"><span>Section <em>groups pages in the reader's navigation</em></span>
          <input value={section} maxLength={60} placeholder="e.g. Foundations"
            onChange={(e) => setSection(e.target.value)} list="pk-sections" />
          <datalist id="pk-sections">
            {groupPages(pack.pages).map(([s]) => <option key={s} value={s} />)}
          </datalist>
        </label>
        <HubNodeEditor
          node={isNew ? null : editingPage}
          api={api}
          heading={isNew ? 'New page in this pack' : 'Edit pack page'}
          onCancel={() => setEditingPage(null)}
          onSaved={async () => { setEditingPage(null); await reload(); }} />
      </div>
    );
  }

  return (
    <div className="ca-panel">
      <div className="ca-panel-row">
        <button className="ca-ghost" type="button" onClick={onBack}>← All packs</button>
        <div className="pk-pubrow">
          <span className={`ca-status ${pack.status}`}>{pack.status === 'published' ? '● Published' : '○ Draft'}</span>
          <button className="ca-primary" type="button" onClick={togglePublish} disabled={busy}>
            {pack.status === 'published' ? 'Unpublish' : 'Publish pack'}
          </button>
        </div>
      </div>
      {err && <div className="ca-error">{err}</div>}
      <p className="ca-hint">
        {pack.status === 'published'
          ? `Live on your storefront and in the Knowledge Hub Library — ${pack.subscriber_count ?? 0} subscriber${(pack.subscriber_count ?? 0) === 1 ? '' : 's'}. Edits go live as you save.`
          : 'A draft only you can see. Publish to list it on your storefront and in every learner\'s Knowledge Hub → Library.'}
      </p>

      <div className="ca-grid2">
        <label className="ca-field"><span>Pack title</span>
          <input value={meta.title} maxLength={120} placeholder="e.g. RAG in Production"
            onChange={(e) => setMeta({ ...meta, title: e.target.value })} onBlur={saveMeta} />
        </label>
        <label className="ca-field"><span>Tagline <em>the one-liner on its card</em></span>
          <input value={meta.tagline} maxLength={300} placeholder="What learners get from this pack"
            onChange={(e) => setMeta({ ...meta, tagline: e.target.value })} onBlur={saveMeta} />
        </label>
      </div>

      <div className="ca-panel-row">
        <div className="ca-panel-h">Pages</div>
        <button className="ca-primary" type="button"
          onClick={() => { setSection(''); setEditingPage('new'); }}>＋ Add page</button>
      </div>
      {(pack.pages || []).length === 0 ? (
        <div className="ca-empty">No pages yet. <b>＋ Add page</b> — write it yourself or paste raw material and let the AI draft it; then generate its question set so learners can practice it.</div>
      ) : groupPages(pack.pages).map(([s, pages]) => (
        <div key={s} className="pk-group">
          <div className="ca-groups-h">{s}</div>
          <div className="ca-list">
            {pages.map((p) => (
              <div key={p.id} className="ca-row">
                <button type="button" className="ca-row-main link" title="Edit this page"
                  onClick={() => { setSection(p.section || ''); setEditingPage(p); }}>
                  <span className="ca-row-t">{p.name}</span>
                  <span className="ca-row-m">
                    {(p.doc?.tabs || []).length} tab{(p.doc?.tabs || []).length === 1 ? '' : 's'} · {(p.questions || []).length} question{(p.questions || []).length === 1 ? '' : 's'} · click to edit
                  </span>
                </button>
                <button type="button" className="ca-del" onClick={() => removePage(p)} aria-label="Delete page">🗑</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── the pack list + creation ────────────────────────────────────────────── */
const PackStudio = () => {
  const [packs, setPacks] = useState(null);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState(null);    // {title, tagline} while creating
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    APIService.creatorPacks().then((d) => setPacks(d.packs || [])).catch((e) => { setErr(e.message); setPacks([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft?.title?.trim()) return;
    try {
      const { pack } = await APIService.creatorCreatePack({ title: draft.title.trim(), tagline: (draft.tagline || '').trim() });
      setDraft(null);
      setOpen(pack);
    } catch (e) { setErr(e.message); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete pack "${p.title}"? Subscribers lose access to it.`)) return;
    try { await APIService.creatorDeletePack(p.id); load(); } catch (e) { setErr(e.message); }
  };

  if (open) {
    return <PackEditor pack={open} onBack={() => { setOpen(null); load(); }} />;
  }

  return (
    <div className="ca-panel">
      <div className="ca-panel-row">
        <div className="ca-panel-h">Study material</div>
        <button className="ca-primary" type="button" onClick={() => setDraft({ title: '', tagline: '' })}>＋ New pack</button>
      </div>
      <p className="ca-hint">Package your own study material — notes, guides, anything worth learning — as a pack of reading pages with practice questions. Publish it and learners subscribe from their Knowledge Hub.</p>
      {err && <div className="ca-error">{err}</div>}

      {draft && (
        <div className="pk-new">
          <label className="ca-field"><span>Pack title</span>
            <input autoFocus value={draft.title} maxLength={120} placeholder="e.g. RAG in Production"
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
          </label>
          <label className="ca-field"><span>Tagline <em>optional</em></span>
            <input value={draft.tagline} maxLength={300} placeholder="What learners get from this pack"
              onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
          </label>
          <div className="ca-foot">
            <button className="ca-ghost" type="button" onClick={() => setDraft(null)}>Cancel</button>
            <button className="ca-primary" type="button" onClick={create} disabled={!draft.title.trim()}>Create pack</button>
          </div>
        </div>
      )}

      {packs === null ? <div className="ca-hint">Loading…</div>
        : packs.length === 0 && !draft ? (
          <div className="ca-empty">No packs yet. <b>＋ New pack</b> starts one — add pages, generate questions, publish.</div>
        ) : (
          <div className="ca-list">
            {packs.map((p) => (
              <div key={p.id} className="ca-row">
                <button type="button" className="ca-row-main link" title="Open this pack"
                  onClick={async () => {
                    try { setOpen((await APIService.creatorGetPack(p.id)).pack); }
                    catch (e) { setErr(e.message); }
                  }}>
                  <span className="ca-row-t">{p.title}</span>
                  <span className="ca-row-m">
                    {p.page_count} page{p.page_count === 1 ? '' : 's'} · {p.subscriber_count} subscriber{p.subscriber_count === 1 ? '' : 's'} · updated {p.updated_at?.slice(0, 10)}
                  </span>
                </button>
                <span className={`ca-status ${p.status}`}>{p.status === 'published' ? '● Published' : '○ Draft'}</span>
                <button type="button" className="ca-del" onClick={() => remove(p)} aria-label="Delete pack">🗑</button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default PackStudio;
